/**
 * @file SideDrawer.tsx
 *
 * Right-anchored slide-in panel for contextual detail (e.g. a product's
 * details). It is a presentational shell: the parent owns open/close state and
 * forwards a ref so it can detect outside clicks. Fixed-width on desktop,
 * full-width slide-in on tablet/mobile (see the co-located stylesheet).
 *
 * Accessibility (non-modal dialog labelled by `label`, hidden while closed,
 * focus moved in on open and restored on close, Escape to dismiss) is provided
 * by {@link useDialogA11y}.
 */

import { forwardRef, type ReactNode } from 'react';
import { useDialogA11y } from '../hooks/use-dialog-a11y';
import styles from './SideDrawer.module.css';

interface SideDrawerProps {
  children: ReactNode;
  /** Whether the drawer is open; drives the slide/expand animation. */
  isOpen: boolean;
  /** Accessible name for the dialog (e.g. the entity being viewed). */
  label?: string;
  /** Called when the drawer is dismissed with the Escape key. */
  onClose?: () => void;
  /** `data-testid` for the drawer root; defaults to `side-drawer`. */
  testId?: string;
}

/**
 * Renders the slide-in drawer shell.
 *
 * @param props.children - The drawer content.
 * @param props.isOpen - Whether the drawer is shown.
 * @param props.label - Accessible name for the dialog.
 * @param props.onClose - Dismiss handler invoked on Escape.
 * @param ref - Forwarded to the drawer root for outside-click detection.
 *
 * @returns The drawer element.
 */
export const SideDrawer = forwardRef<HTMLDivElement, SideDrawerProps>(
  function SideDrawer({ children, isOpen, label, onClose, testId = 'side-drawer' }, ref) {
    const setRef = useDialogA11y(isOpen, onClose, ref);

    return (
      <div
        ref={setRef}
        className={`${styles.sideDrawer} gv-shadow-elevated`}
        data-testid={testId}
        data-state={isOpen ? 'open' : 'closed'}
        role="dialog"
        aria-label={label}
        aria-hidden={isOpen ? undefined : true}
        tabIndex={-1}
      >
        {children}
      </div>
    );
  },
);
